import LoadingFullScreen from "components/common/LoadingFullScreen";
import { LOADING } from "graphql/helpers/queryHelpers";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { useEffect, useState } from "react";

import { useCluster } from "in_queue/contexts/ClusterContext";
import { CustomUpgradesProvider } from "in_queue/contexts/CustomUpgradesContext";
import { Navigate, Outlet } from "react-router-dom";
import { useSubscriptions } from "../../contexts/InQueueSubscriptionsContext";
import {
    clearLastSelectedClusterSlug,
    useSetLastSelectedClusterSlug
} from "../../hooks/useLastClusterStorage";
import { canUserViewCluster } from "../../utils/clusterStatus";

// Wrap any cluster-specific pages. This:
// 1. Ensures the user has access to the cluster, and redirects if not.
// 2. Sets the user's last-viewed cluster.
export const ClusterViewWrapper: React.FC = () => {
    const subscriptions = useSubscriptions();
    const cluster = useCluster();
    const [loading, setLoading] = useState<boolean>(!!cluster);
    const [isPageViewable, setIsPageViewable] = useState<boolean>(false);

    // We only want to set the last selected cluster if the page is viewable.
    // Otherwise, we could end up in a redirect loop trying to return to this
    // unviewable cluster.
    useSetLastSelectedClusterSlug(isPageViewable ? cluster?.slug : undefined);

    useEffect(() => {
        if (!cluster || !subscriptions || subscriptions === LOADING) {
            setLoading(true);
            return;
        }
        // Future: Fetch subscription status as part of the clusters query to
        // remove this.
        setIsPageViewable(
            subscriptions.some((sub) => canUserViewCluster(sub, cluster))
        );
        setLoading(false);
    }, [subscriptions, cluster, setIsPageViewable, setLoading]);

    if (loading) {
        return <LoadingFullScreen />;
    }
    if (!isPageViewable) {
        clearLastSelectedClusterSlug();
        return <Navigate to={`/${IN_QUEUE_PATH}`} />;
    }

    return (
        <CustomUpgradesProvider>
            <Outlet />
        </CustomUpgradesProvider>
    );
};
